import {
    LEGACY_RETURN_TO_HOME_WAITLIST_PAGE,
    RETURN_TO_HOME_PAGE_PREFIX,
    RETURN_TO_HOME_WAITLIST_SUBMIT_QUESTIONS,
} from "./constants";

export type WaitlistData = {
    email: string;
    first_name?: string;
    last_name?: string;
    phone?: string;
    consent?: boolean;
    click_text?: string;
    quiz?: unknown;
};

// sender: SurveyJS Model — `data`/`completeText` are loosely typed in the SDK,
// so answers arrive as `unknown` and are narrowed at the point of access.
type WaitlistSender = {
    data: Record<string, unknown>;
    completeText?: string;
};

// A waitlist question's answer, when it carries contact details, is an object
// of these fields. It can also be a bare email string, or missing entirely.
type WaitlistAnswer = {
    email?: string;
    name?: string;
    lastName?: string;
    phone?: string;
};

const asAnswerObject = (answer: unknown): WaitlistAnswer =>
    answer && typeof answer === "object" ? (answer as WaitlistAnswer) : {};

/**
 * Builds the payload passed to `sendToWaitList` from the survey answers.
 *
 * When a waitlist question is active its answer carries the contact details;
 * otherwise only the quiz answers are forwarded. Shared by the DS quiz
 * variants and the legacy quiz's completion handler.
 */
export const buildWaitlistPayload = (
    waitList: string,
    sender: WaitlistSender
): WaitlistData => {
    if (!waitList) {
        return {
            email: "",
            first_name: "",
            last_name: "",
            phone: "",
            quiz: sender.data,
        };
    }

    const answer = sender.data[waitList];
    const answerObject = asAnswerObject(answer);

    // The answer is either an object with an `email` field or a bare email
    // string. Default to "" so a missing answer or an empty-string email never
    // reaches `toLowerCase()` on the wrong type.
    const rawEmail =
        answerObject.email ?? (typeof answer === "string" ? answer : "");
    const consent = sender.data["consent"];

    return {
        email: rawEmail.toLowerCase(),
        first_name: answerObject.name ?? "",
        last_name: answerObject.lastName ?? "",
        phone: answerObject.phone ?? "",
        consent: Array.isArray(consent) && consent.includes("subscribe"),
        click_text: sender.completeText,
        quiz: sender.data,
    };
};

type PageNavigationSender = {
    currentPage: { name: string };
    visiblePages: { name: string }[];
};

/**
 * Whether the page after the current one is a `return_to_home_*` exclusion screen.
 * The schema places one right after a waitlist page to show it instead of the PQP.
 */
export const isReturnToHomePageNext = (sender: PageNavigationSender): boolean => {
    const currentIndex = sender.visiblePages.findIndex((page) => page.name === sender.currentPage.name);
    const nextPage = currentIndex === -1 ? undefined : sender.visiblePages[currentIndex + 1];

    return Boolean(nextPage?.name.startsWith(RETURN_TO_HOME_PAGE_PREFIX));
};

export const isReturnToHomeExclusionPage = (pageName: string | undefined): boolean => {
    const normalized = pageName ?? "";
    return normalized.startsWith(RETURN_TO_HOME_PAGE_PREFIX)
        || normalized === LEGACY_RETURN_TO_HOME_WAITLIST_PAGE;
};

type PageElement = { name: string };

export const findReturnToHomeWaitlistQuestion = (
    elements: PageElement[] = []
): PageElement | undefined =>
    elements.find((element) =>
        RETURN_TO_HOME_WAITLIST_SUBMIT_QUESTIONS.includes(
            element.name as (typeof RETURN_TO_HOME_WAITLIST_SUBMIT_QUESTIONS)[number]
        )
    );
